'use client';
import { H2, P } from '@osmosis/tailwind-ui/components/Text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { text3, text4, text5 } from '@osmosis/tailwind-ui/styles/text';
import { FormattedMessage } from 'react-intl';
import type { AnswersValuePropArgs } from '../../lib/composer/schemas/answersValueProp';
import { AnswersHeader } from './AnswersHeader';
import AnswerTrialCard from './AnswerTrialCard';
import PublicHeroSearch from '../search/PublicHeroSearch';

const ANSWERS_LANDING_SOURCE_CTA = 'answers-landing-value-prop';

type AnswersLandingItem = {
  id?: number | string;
  title: string;
  url?: string;
};

type ClientComponentProps = {
  items: AnswersLandingItem[];
  valueProp?: AnswersValuePropArgs;
};

/**
 * Client Component.
 */
export const ClientComponent = ({ items, valueProp }: ClientComponentProps) => {
  const { user } = useUserContext();
  const shouldShowValueProp = !user;
  const topicColumnSpan = shouldShowValueProp
    ? 'lg:col-span-2'
    : 'lg:col-span-3';

  return (
    <div className={`flex w-full flex-col items-center ${bg0}`}>
      <AnswersHeader trialCta={valueProp?.trialCta} />

      <Box className="w-full max-w-6xl px-4">
        <PublicHeroSearch
          className="px-0 pb-2 pt-8"
          dataTestId="answers-search-section"
          source="Answers"
        />
      </Box>

      <section
        id="answer-topics"
        className="mx-auto grid w-full max-w-6xl gap-6 px-5 py-10 lg:grid-cols-3 lg:items-start lg:gap-x-8 lg:py-12"
        data-testid="answers-topic-directory"
      >
        <div
          className={`min-w-0 ${topicColumnSpan} lg:col-start-1 lg:row-start-1`}
        >
          <H2 className={`!text-2xl font-bold ${text5}`}>
            <FormattedMessage
              id="answers.client.title"
              defaultMessage="Question and Answer Topics"
            />
          </H2>
          <P className={`mt-2 max-w-3xl ${text3}`}>
            <FormattedMessage
              id="answers.landing.topicDirectoryDescription"
              defaultMessage="Browse the full library of Osmosis answer pages."
            />
          </P>
        </div>

        {shouldShowValueProp && (
          <aside
            className="w-full lg:col-span-1 lg:col-start-3 lg:row-span-2 lg:row-start-1"
            data-testid="answers-value-prop"
          >
            <AnswerTrialCard
              config={valueProp?.trialCard}
              trialCta={valueProp?.trialCta}
              sourceCta={ANSWERS_LANDING_SOURCE_CTA}
              className="mb-0 w-full shadow-sm lg:sticky lg:top-6"
            />
          </aside>
        )}

        <div
          className={`min-w-0 ${topicColumnSpan} lg:col-start-1 lg:row-start-2`}
        >
          <div className="overflow-hidden rounded-lg border border-gray-300">
            <ul className="w-full divide-y divide-gray-200">
              {items.map((item, index) => {
                const href = item.url ? `/answers/${item.url}` : '/answers';

                return (
                  <li key={item.id ?? item.url ?? index}>
                    <a
                      href={href}
                      className={`block px-4 py-3 text-sm font-medium ${text4} transition-colors hover:bg-blue-50 hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600`}
                    >
                      {item.title}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};
